import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchApiGet, fetchApiPost } from "./utils";
import type {
  MarketDataRow,
  SimilarityResponse,
  UserInputValues,
} from "../utils/marketSimilarity/types";
import { toTableRows } from "../utils/marketSimilarity/tableUtils";

export interface FetchHistoricInputsRequest {
  dateString: string; // YYYY-MM-DD
  commodityKey: string;
  numRallyDays: number;
}

export type HistoricInputsResponse = Omit<
  MarketDataRow,
  "close" | "contAdjustedClose" | "rallyScores" | "lighthouseCorrelScores" | "date"
>;

const emptySimilarityResponse: SimilarityResponse = {
  rawData: [],
  clusteredData: [],
  lineChart: [],
};

/**
 * Hook to fetch commodity keys
 */
export function useListCommodityKeys() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["marketSimilarity", "commodityKeys"],
    queryFn: async () => {
      const result = await fetchApiGet<{ data: string[] }>(
        "market_similarity/fetch_commodity_keys",
        queryParams
      );
      return result;
    },
    retry: 1,
  });
}



/**
 * Hook to fetch similarity data
 */
export function useFetchSimilarityData() {
  return useMutation({
    mutationFn: async (inputValues: UserInputValues) => {
      // Clean up euclideanThreshold if it's 0
      const cleanedInputValues = {
        ...inputValues,
        euclideanThreshold:
          inputValues.euclideanThreshold === 0
            ? undefined
            : inputValues.euclideanThreshold,
        lighthouseCorrelThreshold:
          inputValues.lighthouseCorrelThreshold === 0
            ? undefined
            : inputValues.lighthouseCorrelThreshold,
      };

      const result = await fetchApiPost<SimilarityResponse>(
        "market_similarity/fetch_similarity_data",
        cleanedInputValues
      );

      return result;
    },
  });
}

/**
 * Hook to fetch historic inputs for a given date and commodity
 */
export function useFetchHistoricInputs() {
  return useMutation({
    mutationFn: async (request: FetchHistoricInputsRequest) => {
      const result = await fetchApiPost<HistoricInputsResponse>(
        "market_similarity/fetch_historic_inputs",
        request
      );

      return result;
    },
  });
}
